import { useState, type ReactNode } from "react";
import { Alert, Badge, Button } from "../../ds";
import type { DeviceDetail, WgStatus } from "../../lib/api";
import { fmtBytes, relSeconds } from "../../lib/format";
import { useConfirmProbe } from "../../lib/hooks/useConfirmProbe";
import { type ActionNote, useDeviceAction } from "../../lib/hooks/useDeviceAction";
import { useSection } from "../../lib/hooks/useSection";
import { CopyValue } from "../CopyValue";
import { KeyValues } from "../KeyValues";
import { SectionCard } from "../SectionCard";
import { ProbeBanner } from "./ProbeBanner";
import {
  WG_REVERT_IN,
  WgConfirmModal,
  WgUplinkModal,
  type WgForm,
  type WgPending,
} from "./WgUplinkModals";

// Sektion „WireGuard“ → der EulektroWG-Uplink. Der Router ist reiner Client und
// serviert keine Peers; es gibt genau einen Peer, den zentralen WG-Server.
// Geschrieben wird wie bei der Firewall auf Probe (useConfirmProbe).

/** Zuletzt bekannten Stand ins Formular übernehmen. */
const formFromStatus = (status: WgStatus | null): WgForm => ({
  peerPublicKey: status?.peer?.publicKey ?? "",
  endpoint: status?.peer?.endpoint ?? "",
  allowedIps: status?.peer?.allowedIps.join(", ") ?? "",
  keepalive: status?.peer?.persistentKeepalive ? String(status.peer.persistentKeepalive) : "",
  address: status?.address ?? "",
});

const mono = (value: string | null | undefined): ReactNode =>
  value ? <span className="font-mono text-xs">{value}</span> : "—";

function statusItems(s: WgStatus): [string, ReactNode][] {
  const peer = s.peer;
  const handshake = peer?.latestHandshakeSecs ?? null;
  return [
    ["Interface", mono(s.iface)],
    [
      "Zustand",
      <Badge variant={s.up ? "success" : "danger"} dot>
        {s.up ? "up" : "down"}
      </Badge>,
    ],
    ["Eigene Adresse", mono(s.address)],
    ["Eigener Public Key", <CopyValue value={s.publicKey} />],
    [
      "Letzter Handshake",
      <Badge variant={handshake !== null && handshake < 180 ? "success" : "neutral"}>
        {relSeconds(handshake)}
      </Badge>,
    ],
    ["Peer-Endpoint", mono(peer?.endpoint)],
    ["Peer Public Key", peer ? <CopyValue value={peer.publicKey} /> : "—"],
    ["AllowedIPs", mono(peer?.allowedIps.join(", "))],
    ["Keepalive", peer?.persistentKeepalive ? `${peer.persistentKeepalive} s` : "—"],
    ["Empfangen", fmtBytes(peer?.rxBytes)],
    ["Gesendet", fmtBytes(peer?.txBytes)],
  ];
}

/** Nach `rotate_key`: der neue öffentliche Schlüssel muss zentral eingetragen
 *  werden, bevor jemand „Behalten“ drückt. */
function RotatedKey({ publicKey }: { publicKey: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-warning bg-surface p-3">
      <span className="text-xs font-semibold text-content-2">Neuer Public Key des Routers</span>
      <CopyValue value={publicKey} className="text-base" />
      <span className="text-xs text-content-2">
        Diesen Schlüssel <strong>jetzt</strong> auf{" "}
        <span className="font-mono">wireguard.eulektro.de</span> eintragen und erst danach
        „Behalten“ drücken. Ohne Eintrag kommt kein Handshake zustande und der Router rollt nach
        Ablauf der Frist auf das alte Schlüsselpaar zurück.
      </span>
    </div>
  );
}

interface WgRestart {
  latestHandshakeSecs: number | null;
}

/** Der Server sieht nach dem Neustart selbst nach, ob wieder ein Handshake da
 *  ist — „Kommando abgesetzt" wäre wertlos: der häufige Fehlerfall (Endpoint
 *  nicht auflösbar) nimmt `down`/`up` klaglos entgegen und bleibt tot. */
function restartNote(handshake: number | null): ActionNote {
  return handshake === null
    ? {
        variant: "warning",
        text: "Tunnel neu aufgebaut (ifdown/ifup) — weiterhin kein Handshake. Peer-Endpoint, Auflösung und Schlüssel prüfen.",
      }
    : {
        variant: "success",
        text: `Tunnel neu aufgebaut (ifdown/ifup) — Handshake vor ${handshake} s.`,
      };
}

/** Die drei Knöpfe der Sektion — aufsteigend nach Härte: Tunnel neu, Netz neu,
 *  Schlüssel neu. */
function WgActions({
  busy,
  blocked,
  onRestart,
  onRestartNetwork,
  onEdit,
  onRotate,
}: {
  busy: string | null;
  blocked: boolean;
  onRestart: () => void;
  onRestartNetwork: () => void;
  onEdit: () => void;
  onRotate: () => void;
}) {
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        iconName="swap"
        disabled={blocked}
        loading={busy === "wg_restart"}
        onClick={onRestart}
      >
        WireGuard neu starten
      </Button>
      <Button
        size="sm"
        variant="outline"
        iconName="power"
        disabled={blocked}
        loading={busy === "network_restart"}
        onClick={onRestartNetwork}
      >
        Netzwerk neu starten
      </Button>
      <Button size="sm" variant="ghost" iconName="edit" disabled={blocked} onClick={onEdit}>
        Bearbeiten
      </Button>
      <Button
        size="sm"
        variant="danger-outline"
        iconName="key"
        disabled={blocked}
        onClick={onRotate}
      >
        Schlüssel rotieren
      </Button>
    </>
  );
}

export function WireguardSection({ device }: { device: DeviceDetail }) {
  const section = useSection<WgStatus>(device, "wireguard");
  const probe = useConfirmProbe(device.id, section, {
    setAction: "wg_set",
    confirmAction: "wg_confirm",
    revertIn: WG_REVERT_IN,
  });
  // Neustart ändert nichts und läuft deshalb an der Probe-Mechanik vorbei.
  const action = useDeviceAction(device.id);
  const [form, setForm] = useState<WgForm | null>(null);
  const [pending, setPending] = useState<WgPending | null>(null);
  const [rotatedKey, setRotatedKey] = useState<string | null>(null);

  async function restart() {
    const data = await action.run<WgRestart>("wg_restart");
    if (data) action.setNote(restartNote(data.latestHandshakeSecs));
  }

  async function restartNetwork() {
    // Ohne Rückfrage nach dem Handshake: der Neustart kappt die WS-Verbindung,
    // über die zu fragen wäre (docs/device-management.md → network_restart).
    await action.run(
      "network_restart",
      undefined,
      "Netzwerk neu gestartet — der Router ist ~30 s weg. Danach „Vom Router laden“.",
    );
  }

  async function apply() {
    if (!pending) return;
    const params = pending.kind === "rotate" ? { rotate_key: true } : pending.params;
    setPending(null);
    setRotatedKey(null);
    const result = await probe.apply(params);
    if (!result) return;
    setForm(null);
    if (result.publicKey) setRotatedKey(result.publicKey);
  }

  const writeBlocked = !device.online || probe.phase === "probe";

  return (
    <>
      <SectionCard
        title="WireGuard-Uplink (EulektroWG)"
        description="wg show + uci — der Router ist reiner Client mit genau einem Peer"
        section={section}
        deviceOnline={device.online}
        actions={
          <WgActions
            busy={action.busy}
            blocked={writeBlocked}
            onRestart={() => void restart()}
            onRestartNetwork={() => void restartNetwork()}
            onEdit={() => setForm(formFromStatus(section.data))}
            onRotate={() => setPending({ kind: "rotate" })}
          />
        }
        above={
          <>
            {action.note && (
              <Alert
                variant={action.note.variant}
                dismissible
                onDismiss={() => action.setNote(null)}
              >
                {action.note.text}
              </Alert>
            )}
            {probe.note && (
              <Alert variant={probe.note.variant} dismissible onDismiss={() => probe.setNote(null)}>
                {probe.note.text}
              </Alert>
            )}
            <ProbeBanner
              phase={probe.phase}
              secondsLeft={probe.secondsLeft}
              keeping={probe.keeping}
              hint="Der Router hat den geänderten Uplink testweise geladen und stellt den alten automatisch wieder her, wenn niemand bestätigt. Jetzt mit „Vom Router laden“ prüfen, ob wieder ein Handshake zustande kommt."
              onKeep={() => void probe.keep()}
              onDismiss={probe.dismiss}
            >
              {rotatedKey && <RotatedKey publicKey={rotatedKey} />}
            </ProbeBanner>
          </>
        }
      >
        {(status) => <KeyValues items={statusItems(status)} />}
      </SectionCard>

      <WgUplinkModal
        form={form}
        open={pending === null}
        onChange={setForm}
        onClose={() => setForm(null)}
        onSubmit={(params) => setPending({ kind: "set", params })}
      />

      <WgConfirmModal
        pending={pending}
        busy={probe.applying}
        onClose={() => setPending(null)}
        onConfirm={() => void apply()}
      />
    </>
  );
}
